<template>
  <div
    class="project-story lexaday-story project-case-study"
    :style="{ '--case-chrome': chromeHeight + 'px' }"
  >
    <div class="case-hero case-hero-text">
      <ProjectPageHeader project-id="lexaday" context="Personal project">
        <h1>Lexaday</h1>
        <p class="project-subtitle">
          Related words, memorable images, and reviews by email
        </p>
      </ProjectPageHeader>
      <div class="case-hero-lede">
        <p class="lede">
            I built Lexaday to teach related words through a shared root and an AI-generated image. Daily emails introduce new roots and revisit earlier ones on a personalised schedule. The project explores whether one visual memory can help learners recognise meaning across a family of words.
          </p>
      </div>
    </div>
    <ProjectChapterNav
      ref="chapterNav"
      :chapters="chapters"
      :active-chapter="activeChapter"
      :progress="chapterProgress"
      @navigate="goToChapter"
    />
    <article class="project-narrative case-narrative">
      <section id="case-chapter-0" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>01</span>Words & images</span>
          <h2
            id="a-small-lesson-generated-fresh-each-morning"
            data-source-anchor="a-small-lesson-generated-fresh-each-morning"
          >
            Remembering roots through images
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              A vivid image seemed a promising way to make a word’s meaning
              easier to recall. Giving every word its own picture, though,
              would create a new association to learn each time. I was
              interested in whether the same memory could help with several
              words, including ones I had not encountered yet.
            </p>
            <p>
              Roots offered a more compact starting point. Many words share
              an underlying idea such as speaking, hearing or sending. I used
              this shared meaning as the basis for a visual mnemonic, giving
              related words a common image to return to.
            </p>
            <p>
              Each lesson develops one root through those connections. For
              <em>dict</em>, associated with speaking, examples such as
              <em>dictate</em>, <em>contradict</em> and <em>prediction</em>
              show how the meaning takes different forms. A language model
              writes the explanations and examples, and an image model creates
              a visual mnemonic around the root’s meaning. The images here
              come from those lessons.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div
                class="mnemonic-grid"
                aria-label="Lexaday generated mnemonic gallery"
              >
                <figure v-for="image in mnemonicFigures" :key="image.fullSrc">
                  <img data-enlarge :src="image.fullSrc" :alt="image.alt" loading="lazy" decoding="async" />
                  <figcaption><strong>{{ image.label }}</strong><span>{{ image.caption }}</span></figcaption>
                </figure>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-1" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>02</span>Review schedule</span>
          <h2 id="the-learning-loop" data-source-anchor="the-learning-loop">
            Scheduling new roots and reviews
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              I built spaced repetition into the daily emails so earlier roots would return
              alongside new material. Reviews are scheduled after one, two, four, eight and
              progressively more daily runs. Each learner needs a separate schedule because
              the roots they have seen and their last review dates differ. A saved progress
              record lets the job select one unseen root alongside any reviews due that day,
              before generating the lesson.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="process-strip">
                <div class="process-step">
                  <span class="step-num">1</span>
                  <h3 id="pick" data-source-anchor="pick">Due today</h3>
                  <p>
                    The learner's record determines which root is new and which
                    earlier roots need another look.
                  </p>
                </div>
                <div class="process-step">
                  <span class="step-num">2</span>
                  <h3 id="write" data-source-anchor="write">
                    Words in context
                  </h3>
                  <p>
                    A language model writes the introduction and example
                    sentences for the root's related words.
                  </p>
                </div>
                <div class="process-step">
                  <span class="step-num">3</span>
                  <h3 id="imagine" data-source-anchor="imagine">
                    A visual cue
                  </h3>
                  <p>
                    The root's meaning guides an image prompt, which an image
                    model uses to create the mnemonic.
                  </p>
                </div>
                <div class="process-step">
                  <span class="step-num">4</span>
                  <h3 id="send" data-source-anchor="send">The daily email</h3>
                  <p>
                    The lesson and images are assembled into an HTML email, and
                    the progress record is saved for the next run.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-2" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>03</span>Daily lesson</span>
          <h2
            id="what-the-reader-receives"
            data-source-anchor="what-the-reader-receives"
          >
            The daily lesson
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              I packaged the new root and due reviews into a daily email.
              The complete example here shows how the image, related words
              and example sentences form a lesson, followed by earlier roots
              returning for another review.
            </p>
            <div id="how-it-is-packaged" data-source-anchor="how-it-is-packaged">
              <p>
                I used GitHub Actions for the scheduled runs and a JSON file for
                each learner’s progress. The file stores the run count, introduction
                date, last review, and current interval for every root encountered.
                Saving it after a lesson gives the next run the history it needs to
                choose what comes next.
              </p>
              <p>
                The schedule determines which roots the learner receives, and the
                language and image models develop the material for those roots.
                The lesson can vary in its examples and illustrations while
                following a consistent review schedule.
              </p>
              <p class="project-resource-links">
                <GitHubRepoLink
                  href="https://github.com/henryhyw/lexaday"
                  label="Source on GitHub"
                />
              </p>
            </div>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <figure class="email-showcase">
                <ExpandableProjectImage
                  src="/assets/img/lexaday_email.jpg"
                  alt="Lexaday daily vocabulary email screenshot"
                  title="Lexaday daily lesson"
                />
                <figcaption>
                  One day's lesson, including the new root and earlier material
                  due for review.
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>
    </article>
  </div>
</template>
<script>
import { projectById } from '../data/projects';
import { projectFigures } from '../data/projectFigures';
import GitHubRepoLink from "./ui/GitHubRepoLink.vue";
import ProjectPageHeader from "./ui/ProjectPageHeader.vue";
import ExpandableProjectImage from "./ui/ExpandableProjectImage.vue";
import projectCaseStudyMixin from "../mixins/projectCaseStudyMixin";
import ProjectChapterNav from "./ui/ProjectChapterNav.vue";

export default {
  computed: {
    mnemonicFigures() { return projectFigures(projectById('lexaday')).slice(0, 4); },
    chapters() {
      return [
        { id: "case-chapter-0", label: "Words & images" },
        { id: "case-chapter-1", label: "Review schedule" },
        { id: "case-chapter-2", label: "Daily lesson" },
      ];
    },
  },
  name: "LexadayPage",
  components: { ProjectChapterNav, GitHubRepoLink, ProjectPageHeader, ExpandableProjectImage },
  mixins: [projectCaseStudyMixin],
};
</script>
<style scoped>
.project-story {
  --ink: #2f2f2f;
  --muted: #757575;
  --line: #e2e2e2;
  --wash: #f7f7f7;
  --accent: var(--site-text-primary);
}

.lede {
  font-size: 1.08em;
  color: var(--ink);
}

.story-hero {
  margin: 1.7rem 0 2.2rem;
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: linear-gradient(
    180deg,
    rgba(247, 247, 247, 0.92),
    rgba(255, 255, 255, 0.72)
  );
}

.hero-copy {
  margin-bottom: 16px;
}

.eyebrow {
  display: block;
  margin-bottom: 0.35rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted);
}

.hero-copy h2 {
  margin: 0 0 0.7rem;
  font-size: 1.55rem;
  line-height: 1.2;
}

.hero-copy p {
  margin: 0;
  color: #5f5f5f;
}

.mnemonic-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.mnemonic-grid figure { min-width: 0; margin: 0; }
.mnemonic-grid figcaption { margin-top: 8px; font: 12px/1.5 'Quattrocento Sans', sans-serif; color: var(--site-text-secondary); }
.mnemonic-grid figcaption strong { display: block; margin-bottom: 3px; font-size: 15px; font-weight: 600; color: var(--site-text-primary); }
.mnemonic-grid img {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 7px;
  box-shadow: none;
}

.process-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: 1.2rem 0 0.6rem;
}

.process-step {
  border-top: 2px solid var(--accent);
  padding-top: 10px;
}

.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--accent);
  color: var(--site-accent-on-solid);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
}

.process-step h3 {
  margin: 0.55rem 0 0.2rem;
  font-size: 1rem;
}

.process-step p {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--muted);
}

.email-showcase {
  margin: 1.2rem 0 0;
  text-align: center;
}

.email-showcase img {
  max-height: 78vh;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow: none;
  background: #fff;
}

.email-showcase figcaption {
  margin: 0.8rem auto 0;
  max-width: 520px;
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--muted);
}

.feature-list {
  margin: 1rem 0 1.3rem;
  border-top: 1px solid var(--line);
}

.feature-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 18px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line);
}

.feature-row span {
  font-family: "Quattrocento Sans", sans-serif;
  font-weight: 700;
  color: var(--ink);
}

.feature-row p {
  margin: 0;
  color: var(--muted);
}

body.dark-mode .story-hero {
  border-color: #2c2c2c;
  background: linear-gradient(
    180deg,
    rgba(var(--site-accent-shade-rgb), 0.82),
    rgba(15, 15, 15, 0.72)
  );
}

body.dark-mode .project-story {
  --ink: #d7d7d7;
  --muted: #a1a1a1;
  --line: #2e2e2e;
  --accent: var(--site-text-primary);
}

body.dark-mode .hero-copy p,
body.dark-mode .process-step p,
body.dark-mode .feature-row p,
body.dark-mode .email-showcase figcaption {
  color: #a1a1a1;
}

body.dark-mode .email-showcase img,
body.dark-mode .mnemonic-grid img {
  box-shadow: none;
}

@media (max-width: 700px) {
  .mnemonic-grid,
  .process-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .feature-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}

@media (max-width: 420px) {
  .story-hero {
    padding: 14px;
  }

  .hero-copy h2 {
    font-size: 1.35rem;
  }
}
</style>
